import {
  ChevronDown,
  ClipboardCheck,
  FileClock,
  LayoutDashboard,
  LayoutTemplate,
  LifeBuoy,
  LogOut,
  MapPinned,
  Menu,
  Moon,
  ShieldCheck,
  Settings,
  Sun,
  Tags,
  Truck,
  Users,
  UserCog,
  X,
} from "lucide-react";
import * as React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import type { Permission } from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { cn, initials } from "@/lib/utils";
import { useTheme } from "@/theme";

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  /** Shown only if the staff member holds at least one of these. */
  permissions: Permission[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, permissions: [] },
    ],
  },
  {
    label: "Support",
    items: [
      {
        to: "/tickets",
        label: "Tickets",
        icon: LifeBuoy,
        permissions: ["ticket.read"],
      },
      { to: "/users", label: "Users", icon: Users, permissions: ["user.read"] },
      {
        to: "/providers",
        label: "Providers",
        icon: Truck,
        permissions: ["provider.read"],
      },
    ],
  },
  {
    label: "Verification",
    items: [
      {
        to: "/kyc",
        label: "KYC review",
        icon: ShieldCheck,
        permissions: ["kyc.read"],
      },
      {
        to: "/service-review",
        label: "Service review",
        icon: ClipboardCheck,
        permissions: ["service.review"],
      },
    ],
  },
  {
    label: "App content",
    items: [
      {
        to: "/catalog",
        label: "Categories",
        icon: Tags,
        permissions: ["catalog.write"],
      },
      {
        to: "/home-layout",
        label: "Home layout",
        icon: LayoutTemplate,
        permissions: ["home_layout.write"],
      },
    ],
  },
  {
    label: "Platform",
    items: [
      {
        to: "/config",
        label: "Configuration",
        icon: Settings,
        permissions: ["config.read"],
      },
      {
        to: "/service-area",
        label: "Service area",
        icon: MapPinned,
        permissions: ["config.read"],
      },
      {
        to: "/staff",
        label: "Staff",
        icon: UserCog,
        permissions: ["staff.manage"],
      },
      {
        to: "/audit",
        label: "Audit log",
        icon: FileClock,
        permissions: ["audit.read"],
      },
    ],
  },
];

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { canAny } = useAuth();

  const groups = NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => item.permissions.length === 0 || canAny(...item.permissions),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <nav className="flex-1 space-y-5 overflow-y-auto scrollbar-thin px-3 py-4">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {group.label}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground font-medium"
                        : "text-foreground/80 hover:bg-accent hover:text-accent-foreground",
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export default function AppShell() {
  const { me, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-card lg:flex">
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <div className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <span className="text-xs font-bold">K</span>
          </div>
          <span className="text-sm font-semibold tracking-tight">Kheti24</span>
        </div>
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            aria-label="Close menu"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative flex h-full w-64 flex-col border-r border-border bg-card">
            <div className="flex h-14 items-center justify-between border-b border-border px-4">
              <span className="text-sm font-semibold">Kheti24</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(false)}
              >
                <X />
              </Button>
            </div>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-card/95 px-4 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>

          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label={theme === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme === "dark" ? <Sun /> : <Moon />}
            </Button>

            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
              >
                <span className="flex size-7 items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-secondary-foreground">
                  {initials(me?.full_name)}
                </span>
                <span className="hidden max-w-[10rem] truncate sm:inline">
                  {me?.full_name}
                </span>
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </button>

              {menuOpen && (
                <>
                  <button
                    aria-hidden
                    tabIndex={-1}
                    className="fixed inset-0 z-10 cursor-default"
                    onClick={() => setMenuOpen(false)}
                  />
                  <div className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-border bg-popover p-1 shadow-lg animate-slide-up">
                    <div className="border-b border-border px-3 py-2">
                      <p className="truncate text-sm font-medium">{me?.full_name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {me?.email}
                      </p>
                      <p className="mt-1.5 text-[11px] text-muted-foreground">
                        {me?.roles.join(" · ")}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="mt-1 flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-foreground hover:bg-accent"
                    >
                      <LogOut className="size-4" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
